import {useCallback, useEffect, useRef, useState, Suspense, lazy} from 'react'

import {Tabs} from '@primer/react-brand'

import type {GenericContent, GenericGroup} from '../../../../../brand/lib/types/contentful'

import {AutoPlayVideo} from '../../_components/AutoPlayVideo/AutoPlayVideo'

import ImageBgDesktop from '../../_assets/hero-bg-desktop.webp'

import styles from './HeroTabs.module.css'

const CopilotHeadWebGL = lazy(() => import('../../_components/CopilotHeadWebGL/CopilotHeadWebGL'))

type Props = {
  contentfulContent: GenericGroup
}

export function HeroTabs(props: Props) {
  const {contentfulContent} = props
  const {content} = contentfulContent.fields as {
    content: GenericContent[]
  }

  const [activeTab, setActiveTab] = useState<string>('0')

  const handleTabChange = useCallback((id: string) => {
    setActiveTab(id)
  }, [])

  const tabs = content.map(item => ({
    id: item.sys.id,
    title: item.fields.heading,
    video: {
      src: item.fields.media?.at(0)?.fields.asset.fields.file.url,
      description: item.fields.media?.at(0)?.fields.asset.fields.description,
    },
  }))

  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])

  // Stop and restart videos when tab changes
  useEffect(() => {
    const activeIndex = parseInt(activeTab, 10)

    for (const [index, video] of videoRefs.current.entries()) {
      if (!video) continue

      if (index === activeIndex) {
        video.currentTime = 0
        // eslint-disable-next-line github/no-then
        video.play().catch(() => {})
      } else {
        video.pause()
        video.currentTime = 0
      }
    }
  }, [activeTab])

  return (
    <div className={styles.container}>
      {tabs.map((tab, index) => (
        <div
          key={`${tab.id}video`}
          id={`panel-${index}`}
          aria-labelledby={`tab-${index}`}
          role="tabpanel"
          tabIndex={0}
          hidden={activeTab !== `${index}`}
          className={styles.panel}
          ref={el => {
            const video = el?.querySelector('video')
            if (video) videoRefs.current[index] = video
          }}
        >
          {tab.video.src ? (
            <>
              <img src={ImageBgDesktop} alt="" className={styles.backgroundImage} />

              <AutoPlayVideo src={tab.video.src} poster="" width="1248" height="735" className={styles.video} />
            </>
          ) : null}
        </div>
      ))}

      <Tabs onChange={handleTabChange} aria-label="Hero video tabs" className={styles.tabs}>
        {tabs.map((tab, index) => (
          <Tabs.Item key={`${tab.id}title`} id={`tab-${index}`} aria-controls={`panel-${index}`}>
            {tab.title}
          </Tabs.Item>
        ))}
      </Tabs>

      <Suspense>
        <CopilotHeadWebGL />
      </Suspense>
    </div>
  )
}
